import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { IonApp } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { ThemeProvider } from '@material-ui/core/styles';
import * as Sentry from '@sentry/react';
import { IntlProvider } from 'react-intl';
import { SnackbarProvider } from 'notistack';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

import './theme/variables.css';
import './theme/global.css';
import theme, { backgroundColor } from './app/lib/theme';
import { reportIntlError } from './app/lib/helpers/intl-error.helpers';

// Componentss
import { PARENT_HOME_ROUTE } from './_configuration';
import { PARENT_ROUTES, STAFF_ROUTES } from './_routes';
import { SessionBootstrap } from './app/components/_core/PersistGateContainer/persist-gate.container';
import { AppApolloProvider } from './app/components/_core/PersistGateContainer/app-apollo-provider.component';
import { SessionType } from './app/lib/types/session.types';
import { OfflineHydrator } from './app/components/_core/OfflineHandler/offline-handler.container';
import UsetifulProvider from './app/components/_core/UsetifulProvider/usetiful-provider.component';
import { CalibrationAssignmentsPoller } from './app/components/_core/CalibrationAssignmentsPoller/calibration-assignments-poller.component';
import { SelectionTargetGate } from './app/components/_core/SelectionTargetGate/selection-target-gate.component';
import { IdleSignOut } from './app/components/_core/IdleSignOut/idle-sign-out.component';

if (process.env.REACT_APP_SENTRY_ENV && process.env.REACT_APP_SENTRY_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY,
    environment: process.env.REACT_APP_SENTRY_ENV,
    integrations: [
      new Sentry.BrowserTracing()
    ],
    tracesSampleRate: process.env.REACT_APP_SENTRY_ENV === 'production' ? 0.1 : 1.0
  });
}

export const App: React.FC = () => (
  <SessionBootstrap>
    <UsetifulProvider>
      <ThemeProvider theme={theme}>
        <IntlProvider locale="en" onError={reportIntlError}>
          {/* Snackbar content renders at this provider's tree position, so it must sit
              inside IntlProvider/ThemeProvider for enqueued JSX to see intl + theme. */}
          <SnackbarProvider autoHideDuration={3000} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <IonApp style={{ background: backgroundColor }}>
            <IonReactRouter>
              <Switch>
                {/* Two protected apps, each with its own ApolloClient (isolated cache
                    + own token). The parent app lives under /parent; everything else
                    is staff. Offline sync is staff-only. */}
                <Route path={PARENT_HOME_ROUTE} render={() => (
                  <AppApolloProvider tree={SessionType.PARENT}>
                    <Switch>{PARENT_ROUTES}</Switch>
                  </AppApolloProvider>
                )} />
                <Route render={() => (
                  <AppApolloProvider tree={SessionType.STAFF}>
                    <OfflineHydrator>
                      <SelectionTargetGate>
                        {/* Staff-only in-app calibration assignment poller + nudge. */}
                        <CalibrationAssignmentsPoller />
                        <IdleSignOut />
                        <Switch>{STAFF_ROUTES}</Switch>
                      </SelectionTargetGate>
                    </OfflineHydrator>
                  </AppApolloProvider>
                )} />
              </Switch>
            </IonReactRouter>
          </IonApp>
          </SnackbarProvider>
        </IntlProvider>
      </ThemeProvider>
    </UsetifulProvider>
  </SessionBootstrap>
);
